import { PlusCircleOutlined } from '@ant-design/icons';
import { Modal, Select, Typography, Space, Row, Tag, Empty, Spin, message, Input } from 'antd';
import { uniqBy } from 'lodash-es';
import React, { FC, useEffect, useState } from 'react';
import fileExcelHistory from '@/assets/images/public/file-excel-history.svg';
import IconSearch from '@/assets/images/public/icon-search.svg';
import { getUserId } from '@/infrastructure/auth';
import { getProductId, getPid } from '@/infrastructure/product-info';
import { storageGet, storageSet } from '@/infrastructure/storage-utils';
import { getTagList, TagInfo } from '@/presentation/pages/setting/tag-manage/api';
import { getColorLabel } from '@/presentation/pages/setting/tag-manage/columns';
import { TAG_COLOR_INFO } from '@/presentation/pages/setting/tag-manage/config';
import { useStores } from '@/presentation/store';
import { MetricType } from '@/proto/metric';
import style from './index.module.scss';
import { useTranslation } from 'react-i18next';

interface IProps {
  visible: boolean;
  onSubmit: (tagNames: ITagName[]) => void;
  setModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  metricType?: MetricType;
}

interface OptionsType {
  label: string | React.ReactNode;
  value: string;
  key: string;
  color: string;
}

interface LocalTag {
  name: string;
  color: string;
  backgroundColor: string;
  borderColor: string;
}

export interface ITagName {
  label: React.ReactNode | string;
  value: string;
  color: string;
  tagId?: string;
}

export const AddTagModal: FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { visible, onSubmit, setModalVisible } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [tagList, setTagList] = useState<TagInfo[]>([]);
  const [options, setOptions] = useState<OptionsType[]>([]);

  const [initOptions, setInitOptions] = useState<OptionsType[]>([]);
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const [tagNames, setTagNames] = useState<ITagName[]>([]);
  const [open, setOpen] = useState<boolean | undefined>(undefined);
  const [localTagInfos, setLocalTagInfos] = useState<LocalTag[] | null>(null);
  const { getOptions } = useStores().IssueStore;

  useEffect(() => {
    if (!open) {
      setSearchValue(undefined);
    }
  }, [open]);

  const loadTagList = async () => {
    setLoading(true);
    setTagNames([]);
    const tagList = await getTagList();
    const options = tagList.map(item => ({
      label: item.tagName,
      value: item.tagName,
      key: item.tagName,
      color: item.tagColor,
    })) as OptionsType[];
    setTagList(tagList);
    setOptions(options);
    setInitOptions(options);
    setLoading(false);
  };

  const getLocalTags = () => {
    try {
      const localTagInfos = JSON.parse(storageGet(`${getUserId()}_tags`)!);
      const newLocalTagInfo = localTagInfos.map((localTagInfo: LocalTag) => {
        const tagColor = tagList?.find(tag => tag.tagName === localTagInfo.name)?.tagColor;
        const findTagInfo = TAG_COLOR_INFO.find(item => item.color === tagColor);
        return {
          ...localTagInfo,
          color: findTagInfo?.color ?? localTagInfo.color,
          backgroundColor: findTagInfo?.backgroundColor ?? localTagInfo.backgroundColor,
          borderColor: findTagInfo?.borderColor ?? localTagInfo.borderColor,
        };
      });
      setLocalTagInfos(newLocalTagInfo as LocalTag[]);
      storageSet(`${getUserId()}_tags`, JSON.stringify(newLocalTagInfo));
    } catch (e) {
      setLocalTagInfos(null);
    }
  };

  const setLocalStorageTag = () => {
    const tagInfo = localTagInfos || [];
    const filterTags = tagNames?.filter(tagName => !tagInfo?.some(item => item.name === tagName.value));
    if (!filterTags?.length) {
      return;
    }
    const findTagInfos = filterTags.map((filterTag) => {
      const colorInfo = TAG_COLOR_INFO.find((item) => {
        const color = filterTag.color ?? TAG_COLOR_INFO[0].color;
        return item.color === color;
      }) ?? TAG_COLOR_INFO[0];
      return {
        name: filterTag.value,
        color: colorInfo.color,
        backgroundColor: colorInfo.backgroundColor,
        borderColor: colorInfo.borderColor,
      };
    }) ;
    storageSet(`${getUserId()}_tags`, JSON.stringify(tagInfo.concat(findTagInfos)));
  };


  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setTagColor = (color: string, value: string) => {
    setTagNames(tagNames => tagNames.map(tag => (tag.value === value ? { ...tag, color } : tag)));
    setOptions(options => options?.map((item) => {
      if (item.value === value) {
        return {
          ...item,
          color,
          label: <CreateTag
            color={color}
            value={value}
            setTagColor={setTagColor}
            setOpen={setOpen}/>,
        };
      }
      return item;
    }));
  };

  useEffect(() => {
    if (visible) {
      loadTagList();
    }
  }, [visible]);

  useEffect(() => {
    if (visible && !!tagList.length) {
      getLocalTags();
    }
  }, [visible, tagList]);

  const onSearch = (value: string) => {
    setSearchValue(value);

    setOptions((options) => {
      const filterOptions = options.filter(item => !initOptions.some(option => option.value === value)
          && tagNames.some(option => option.value === item.value)) || [];
      if (initOptions.some(option => option.value === value)) {
        return [...initOptions, ...filterOptions];
      }
      if (value && !filterOptions.some(option => option.value === value)) {
        return [...initOptions, ...filterOptions, {
          label: <CreateTag
              color={TAG_COLOR_INFO[0].color}
              value={value}
              setTagColor={setTagColor}
              setOpen={setOpen}/>,
          value,
          color: TAG_COLOR_INFO[0].color,
          key: value,
        }];
      }
      return [...initOptions, ...filterOptions];
    });
  };

  const tagNameChange = (values: ITagName[]) => {
    const uniqByValues = uniqBy(values, 'value');
    if ((uniqByValues?.length || 0) > 30) {
      message.warning(t("标签最多30个字符"));
      return;
    }


    setOptions((options) => {
      const filterOptions = uniqByValues?.filter(item => !initOptions
        ?.some(optionValue => item.value
          === optionValue?.value) && options?.some(optionValue => item.value
        === optionValue?.value))?.map((item) => {
        const option = options?.find(optionValue => item.value === optionValue.value);
        return { ...option!,
          label: <CreateTag
            color={option?.color || ''}
            value={option?.value || ''}
            setTagColor={setTagColor}
            setOpen={setOpen}/> };
      });

      return [...(initOptions || []), ...(filterOptions || [])];
    });

    setTagNames(uniqByValues?.map((item) => {
      const tagInfo = tagList.find(tag => tag.tagName === item.value);
      if (tagInfo && !item?.tagId) {
        return {
          ...item,
          tagId: tagInfo.tagId,
        };
      }
      return { ...item, color: TAG_COLOR_INFO[0].color };
    }));
  };

  return <Modal
    title={t("添加标签")}
    visible={visible}
    onOk={async () => {
      await onSubmit(tagNames);
      setLocalStorageTag();

      // 更新store的标签
      props.metricType && getOptions(props.metricType, true);
    }}
    onCancel={() => {
      setModalVisible(false);
    }}
  >
    <Spin spinning={loading}>
      <Space direction={'vertical'}  size={3} style={{ width: '100%' }}>
          <Select
            className={'mb-[10px]'}
            menuItemSelectedIcon={null}
            searchValue={searchValue}
            onSearch={onSearch}
            optionLabelProp={'value'}
            labelInValue
            mode={'tags'}
            style={{ width: '465px' }}
            options={options}
            onChange={tagNameChange}
            optionFilterProp={'value'}
            value={tagNames}
            showSearch
            onFocus={() => setOpen(true)}
            open={open}
            onBlur={() => {
              setOpen(false);
            }}
            placeholder={t("搜索或创建标签")}
          >
            <Input suffix={<img src={IconSearch} alt={''}/>} placeholder={t("搜索或创建标签")}/>
          </Select>
        <Row style={{ width: '100%' }}>
          <Typography.Text style={{ display: 'flex', alignItems: 'center' }}>
            <img src={fileExcelHistory} alt={''}/>
            {t("&nbsp;最近使用标签")}
          </Typography.Text>
        </Row>
        <Row>
          {!!localTagInfos?.length ? localTagInfos.map((item: any) => <Tag key={item.name}
            onClick={() => {
              setTagNames((names) => {
                const info = tagList.find(tag => tag.tagName === item.name);
                if (item.name && !names.some(name => name.value === item.name)) {
                  return [
                    ...names,
                    { label: item?.name || '',
                      value: item?.name || '',
                      color: item?.color || '',
                      tagId: info?.tagId || '' }];
                }
                return names;
              });
            }}
            style={{
              backgroundColor: item.backgroundColor,
              color: item.color,
              borderColor: item.borderColor,
              marginRight: '12px',
              marginTop: '10px',
              cursor: 'pointer',
            }}>
            {item.name}
          </Tag>) : <Empty description={t("暂无最近使用标签")} style={{ width: '100%' }}/>}
        </Row>
        <Row style={{ marginTop: '100px' }}>
          <a
            style={{ textDecoration: 'underline' }}
            href={`${location.origin}/v2/setting/product-config?tab=tag&productId=${getProductId()}&pid=${getPid()}`}
          >
            {t("查看标签管理")}
          </a>
        </Row>
      </Space>
    </Spin>
  </Modal>;
};


const CreateTag = (props: {
  value: string, color: string,
  setTagColor: (color: string, value: string) => void,
  setOpen: React.Dispatch<React.SetStateAction<boolean | undefined>>
}) => {
  const { t } = useTranslation();
  const { value, setTagColor, color, setOpen } = props;
  const colorOptions = TAG_COLOR_INFO.map(item => ({
    label: getColorLabel(item.color),
    value: item.color,
  }));
  return <div
  style={{
    color: '#0A6CFF',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  }}
>
  <div style={{ width: '350px' }} onClick={() => {
    setOpen(undefined);
  }}>
    <PlusCircleOutlined style={{ marginRight: '8px' }}/>
    {t("创建标签")} {value}
  </div>
  <Select
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}
    onMouseDown={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}
    className={style.selectColor}
    options={colorOptions}
    onFocus={() => setOpen(true)}
    onBlur={() => setOpen(undefined)}
    onChange={(color) => {
      setTagColor(color, value);
    }}
    value={color}
    defaultValue={TAG_COLOR_INFO[0].color}
  />
</div>;
};
